import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import memoize from 'lodash.memoize';
import flow from 'lodash.flow';
import curry from 'lodash.curry';
import {
  loadReport,
  generateReport,
  loadReportLookupDataSource,
  loadBrowser,
  loadBrowserData
} from '../../actions/actionCreators/reportsActions';
import { getReport, getReportLoading, getReportLoaded, getReportGenerating } from '../../selectors/reports';
import Report from './Report';
import * as editableDocument from 'utils/model/editableDocument';
import { isMandatory, foreach } from 'utils/simple-validations';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import WrappedFakeText from 'components/common/WrappedFakeText';

const addFiltersPrefix = memoize(addPropertyPrefix('filters'));

const validationRules = {
  filters: foreach(filter => ({
    value: [!filter.allowNull && isMandatory()]
  }))
};

class ReportCardContainer extends PureComponent {
  state = {
    inEditReport: null
  };
  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.inEditReport && nextProps.reportLoaded && nextProps.report)
      return { inEditReport: editableDocument.create(nextProps.report) };

    return null;
  }

  componentDidMount() {
    this.props.actions.loadReport(this.props.reportId);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.reportLoaded && this.props.reportLoaded && this.props.report) {
      this.props.pushReportName(this.props.report.reportName);
    }
  }

  handlePropertyChange = curry((prop, value) => {
    this.setState(currentState => {
      return {
        inEditReport: flow(
          editableDocument.update(prop, value, null),
          editableDocument.validate(validationRules, true)
        )(currentState.inEditReport)
      };
    });
  });

  handleGenerateReport = () => {
    this.setState(
      currentState => {
        const report = editableDocument.validate(validationRules, false, currentState.inEditReport);
        if (!report.validationInfo.isValid) {
          return {
            inEditReport: report
          };
        }
      },
      () => {
        if (this.state.inEditReport.validationInfo.isValid) {
          this.props.actions.generateReport(this.state.inEditReport.document);
        }
      }
    );
  };

  loadOptions = reportFilterId => {
   return this.props.actions.loadReportLookupDataSource(this.props.report.reportId, reportFilterId);
  };

  loadBrowser = curry((reportFilterId, browserId) => {
    this.props.actions.loadBrowser(reportFilterId, this.props.report.reportId, browserId);
  });

  loadBrowserData = curry((reportFilterId, filters, page, pageSize, browserId, order, orderBy, orderByFieldName, reload) => {
    this.props.actions.loadBrowserData(
      filters,
      page,
      pageSize,
      browserId,
      reportFilterId,
      this.props.report.reportId,
      order,
      orderBy,
      orderByFieldName,
      reload
    );
  });

  render() {
    const { reportLoaded, reportGenerating } = this.props;
    const { inEditReport } = this.state;

    return (
      <>
        {reportLoaded ? (
          <Report
            report={inEditReport}
            onPropertyChange={addFiltersPrefix(this.handlePropertyChange)}
            onGenerateReport={this.handleGenerateReport}
            reportGenerating={reportGenerating}
            loadOptions={this.loadOptions}
            loadBrowser={this.loadBrowser}
            loadBrowserData={this.loadBrowserData}
          />
        ) : (
          <WrappedFakeText lines={5} />
        )}
      </>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const report = getReport(ownProps.reportId, state);
  const reportLoading = getReportLoading(ownProps.reportId, state);
  const reportLoaded = getReportLoaded(ownProps.reportId, state);
  const reportGenerating = getReportGenerating(ownProps.reportId, state);

  return {
    report,
    reportLoading,
    reportLoaded,
    reportGenerating
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ loadReport, generateReport, loadReportLookupDataSource, loadBrowser, loadBrowserData }, dispatch)
  };
}

ReportCardContainer.propTypes = {
  reportLoaded: PropTypes.bool,
  report: PropTypes.object,
  actions: PropTypes.object,
  reportId: PropTypes.number,
  pushReportName: PropTypes.func.isRequired,
  reportGenerating: PropTypes.bool
};

const ReportCardContainerConnected = connect(mapStateToProps, mapDispatchToProps)(ReportCardContainer);

export default ReportCardContainerConnected;
